/**
 * Form-cache IndexedDB store（完整 payload；禁止用 localStorage）。
 * Record 形狀對齊 handoff `LocalFormCacheRecord`（FE camelCase）。
 *
 * @typedef {object} LocalFormCacheRecord
 * @property {string} key
 * @property {string} scope
 * @property {1} payloadVersion
 * @property {object} payload
 * @property {number} serverRevision
 * @property {string | null} serverSnapshotId
 * @property {boolean} dirty
 * @property {number} localSavedAtEpochMs
 */

const DB_NAME = 'chenyo-erp-form-cache';
const DB_VERSION = 1;
const STORE_NAME = 'records';

function getIndexedDb() {
  if (typeof indexedDB === 'undefined') {
    throw new Error('IndexedDB is not available in this environment');
  }
  return indexedDB;
}

/**
 * @returns {Promise<IDBDatabase>}
 */
function openFormCacheDb() {
  const idb = getIndexedDb();
  return new Promise((resolve, reject) => {
    const request = idb.open(DB_NAME, DB_VERSION);
    request.onerror = () => {
      reject(request.error ?? new Error('Failed to open form-cache IndexedDB'));
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
  });
}

/**
 * @template T
 * @param {'readonly' | 'readwrite'} mode
 * @param {(store: IDBObjectStore) => IDBRequest} run
 * @returns {Promise<T>}
 */
async function withStore(mode, run) {
  const db = await openFormCacheDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const store = tx.objectStore(STORE_NAME);
      const request = run(store);
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        reject(request.error ?? new Error('IndexedDB request failed'));
      };
    });
  } finally {
    db.close();
  }
}

/**
 * @param {string} key
 * @returns {Promise<LocalFormCacheRecord | null>}
 */
export async function getLocalFormCacheRecord(key) {
  const row = await withStore('readonly', (store) => store.get(key));
  return row ?? null;
}

/**
 * @param {LocalFormCacheRecord} record
 * @returns {Promise<LocalFormCacheRecord>}
 */
export async function putLocalFormCacheRecord(record) {
  if (!record?.key) {
    throw new Error('LocalFormCacheRecord.key is required');
  }
  await withStore('readwrite', (store) => store.put(record));
  return record;
}

/**
 * @param {string} key
 * @returns {Promise<void>}
 */
export async function deleteLocalFormCacheRecord(key) {
  await withStore('readwrite', (store) => store.delete(key));
}
